import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../auth/authStore';
import { useCombatStore } from '../../store/combatStore';
import { useWalletStore } from '../../store/walletStore';
import { authApi, partyApi, type Character } from '../../auth/authApi';
import { fetchWallet } from '../../api/walletApi';
import { useParty } from '../../hooks/useParty';
import FriendsSection from './FriendsSection';
import PartySection from './PartySection';
import PlayerToolbar from './PlayerToolbar';
const profileIcon = '/icons/profile.svg';
import CharacterStatsModal from './CharacterStatsModal';
import WalletHistoryModal from './WalletHistoryModal';
import HubMap from './HubMap';
import { useHeartbeat } from '../../hooks/useHeartbeat';
import { useTranslation } from '../../i18n/useTranslation';
import styles from './UserPage.module.css';

// ─────────────────────────────────────────────────────────────────────────────
// Class metadata
// ─────────────────────────────────────────────────────────────────────────────

const CLASS_META = {
  warrior: { label: 'Guerreiro',  icon: '⚔',  color: '#c87c30' },
  mage:    { label: 'Mago',       icon: '🔮', color: '#8060d0' },
  rogue:   { label: 'Ladino',     icon: '🗡',  color: '#40a060' },
  archer:  { label: 'Arqueiro',   icon: '🏹', color: '#30a0c0' },
  cleric:  { label: 'Clérigo',    icon: '✨', color: '#e0c040' },
  ferreiro:{ label: 'Ferreiro',   icon: '🔨', color: '#9a8478' },
} as const;

const CLASSES = [
  { id: 'warrior', label: 'Guerreiro',  icon: '⚔' },
  { id: 'mage',    label: 'Mago',       icon: '🔮' },
  { id: 'rogue',   label: 'Ladino',     icon: '🗡' },
  { id: 'archer',  label: 'Arqueiro',   icon: '🏹' },
  { id: 'cleric',  label: 'Clérigo',    icon: '✨' },
  { id: 'ferreiro',label: 'Ferreiro',   icon: '🔨' },
] as const;

// ─────────────────────────────────────────────────────────────────────────────

export default function UserPage() {
  const navigate = useNavigate();
  const { user, token, logout } = useAuthStore();
  const { t, language, setLanguage } = useTranslation();

  // Keep the server's onlineAt fresh for party presence checks
  useHeartbeat(token);

  // Estado da equipe — compartilhado com o PartySection do painel direito
  // (uma única sondagem) e usado aqui pra avisar antes de batalhar sozinho.
  const partyState = useParty(token);
  const myParty = partyState.party;

  const [character,   setCharacter]   = useState<Character | null | undefined>(undefined);
  const [charLoading, setCharLoading] = useState(true);
  // Saldo "Ta Feito": store (atualizado em tempo real por Mercador/Forja) com
  // fallback no fetch inicial.
  const [walletBalanceLocal, setWalletBalanceLocal] = useState<number>(0);
  const storeBalance = useWalletStore(s => s.balance);
  const setBalanceStore = useWalletStore(s => s.setBalance);
  const walletBalance = storeBalance ?? walletBalanceLocal;

  // Creation form state
  const [charName,  setCharName]  = useState('');
  const [charClass, setCharClass] = useState<string>('warrior');
  const [creating,  setCreating]  = useState(false);
  const [createErr, setCreateErr] = useState<string | null>(null);

  const [battling,   setBattling]   = useState(false);
  const [battleErr,  setBattleErr]  = useState<string | null>(null);
  const [battleInfo, setBattleInfo] = useState<string | null>(null);

  // Energy state
  const [gameConfig, setGameConfig] = useState<{maxEnergy: number, energyRechargeRate: number, battleEnergyCost: number} | null>(null);
  const [baseEnergy, setBaseEnergy] = useState<number>(0);
  const [baseTime, setBaseTime] = useState<number>(0);
  const [visualEnergy, setVisualEnergy] = useState<number>(0);

  // Modal State
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [showWalletHistory, setShowWalletHistory] = useState(false);

  // Hub Map state
  const [hubState, setHubState] = useState<any>(null);

  // ── Load character on mount ────────────────────────────────────────────────
  useEffect(() => {
    if (!token) return;
      authApi.getCharacter(token)
        .then(res => {
          setCharacter(res.character);
          if (res.game_config) {
            setGameConfig(res.game_config);
          }
          if (res.character) {
            setBaseEnergy(res.character.currentEnergy ?? 0);
            setBaseTime(res.character.lastEnergyUpdate ? new Date(res.character.lastEnergyUpdate).getTime() : Date.now());
            setVisualEnergy(res.character.currentEnergy ?? 0);
          }
        })
        .catch(() => setCharacter(null))
        .finally(() => setCharLoading(false));

      fetchWallet(token)
        .then(res => { setWalletBalanceLocal(res.balance); setBalanceStore(res.balance); })
        .catch(() => setWalletBalanceLocal(0));

      fetch('/api/user/hub/state', { 
        headers: { 'Authorization': `Bearer ${token}` },
        cache: 'no-store'
      })
        .then(r => r.ok ? r.json() : null)
        .then(data => {
          if (data && data.config) {
            setHubState(data);
          }
        })
        .catch(() => {});
    }, [token]);

  // Volta de "Nova Batalha" sem energia (App.tsx) → mostra o aviso do servidor.
  useEffect(() => {
    const msg = sessionStorage.getItem('battle_start_error');
    if (msg) {
      sessionStorage.removeItem('battle_start_error');
      setBattleErr(msg);
    }
  }, []);

  // Poção usada (InventorySection) → recalibra a barra de energia na hora.
  useEffect(() => {
    const onEnergy = (e: Event) => {
      const val = (e as CustomEvent).detail;
      if (typeof val === 'number') {
        setBaseEnergy(val);
        setBaseTime(Date.now());
        setVisualEnergy(val);
      }
    };
    window.addEventListener('energy-changed', onEnergy);
    return () => window.removeEventListener('energy-changed', onEnergy);
  }, []);

  // ── Energy Regeneration Tick ──────────────────────────────────────────────
  useEffect(() => {
    if (!gameConfig || !character) return;
    const interval = setInterval(() => {
      const now = Date.now();
      const elapsedSec = Math.floor((now - baseTime) / 1000);
      const regenerated = Math.floor(elapsedSec / gameConfig.energyRechargeRate);
      const current = Math.min(gameConfig.maxEnergy, baseEnergy + regenerated);
      setVisualEnergy(current);
    }, 1000);
    return () => clearInterval(interval);
  }, [gameConfig, character, baseEnergy, baseTime]);

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleLogout = async () => {
    await logout();
    navigate('/', { replace: true });
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;
    setCreating(true);
    setCreateErr(null);
    try {
      const res = await authApi.createCharacter(token, charName, charClass);
      setCharacter(res.character);
      const current = useAuthStore.getState().user;
      if (current) {
        useAuthStore.setState({ user: { ...current, characterId: res.character.id } });
      }
    } catch (e) {
      setCreateErr((e as Error).message);
    } finally {
      setCreating(false);
    }
  };

  const handleBattle = async (fromBattlePoint = false) => {
    if (!token || battling) return;

    // Batalha solo estando em equipe: confirma e sai da equipe primeiro.
    // O backend também remove ao iniciar, mas aqui a UI já reflete na hora.
    if (myParty) {
      if (!window.confirm(t('party.confirmLeaveSolo'))) return;
      try {
        await partyApi.leave(token);
        await partyState.reload();
      } catch { /* o backend ainda tira o jogador da equipe ao iniciar */ }
    }

    setBattling(true);
    setBattleErr(null);
    setBattleInfo(null);

    try {
      const res = await authApi.startBattle(token, fromBattlePoint);
      setBattleInfo(`${res.map_name} · ${res.enemy_count} inimigo${res.enemy_count !== 1 ? 's' : ''}`);

      // Brief pause so the user can read the match info before navigating
      await new Promise(r => setTimeout(r, 900));

      // Clear any stale combat state from a previous battle so App.tsx
      // doesn't briefly flash the old result overlay on mount.
      useCombatStore.getState().resetCombat();

      navigate(`/play?combat_id=${res.combat_id}&entity_id=${res.player_entity_id}`);
    } catch (e) {
      setBattleErr((e as Error).message);
      setBattling(false);
    }
  };

  // Wrapper para o HubMap — sempre envia from_battle_point=true
  const handleHubBattle = () => handleBattle(true);

  const hasEnoughEnergy = gameConfig ? visualEnergy >= gameConfig.battleEnergyCost : true;

  // ── Barra de energia da navbar (ao lado da moeda) ─────────────────────────
  const energyPct = gameConfig
    ? Math.min(100, Math.max(0, (visualEnergy / gameConfig.maxEnergy) * 100))
    : 0;
  const secsToFull = gameConfig
    ? Math.max(0, (gameConfig.maxEnergy - Math.floor(visualEnergy)) * gameConfig.energyRechargeRate)
    : 0;
  const fmtDur = (s: number): string => {
    if (s <= 0) return t('energy.full' as any) || 'cheia';
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    if (h > 0) return `${h}h ${m}min`;
    if (m > 0) return `${m}min`;
    return `${s}s`;
  };

  // ─────────────────────────────────────────────────────────────────────────

  const classMeta = character
    ? CLASS_META[character.class as keyof typeof CLASS_META]
    : null;

  // Fim de missão pode pedir uma vista específica (Hub ou painel) uma única vez;
  // senão vale a configuração global do Hub (initialViewMode).
  const [viewOverride] = useState(() => {
    try {
      const v = sessionStorage.getItem('user_view_override');
      sessionStorage.removeItem('user_view_override');
      return v;
    } catch { return null; }
  });
  const isMapView = !!hubState?.config && (viewOverride ? viewOverride === 'map' : hubState.config.initialViewMode === 'map');

  return (
    <div className={styles.page}>

      {/* ── Navbar ── */}
      <nav className={styles.nav}>
        <span className={styles.navBrand}>⚔ Dark Age</span>
        {/* Admin entrando como jogador (via botão "Mapa Hub" do painel admin)
            pra verificar o Hub de verdade — precisa de um jeito óbvio de voltar. */}
        {user?.role === 'admin' && (
          <a href="/admin" style={{
            display: 'flex', alignItems: 'center', gap: 6, marginLeft: 16,
            fontSize: 12, fontWeight: 600, color: '#e8c87a', textDecoration: 'none',
            border: '1px solid rgba(232,200,122,0.4)', borderRadius: 6, padding: '4px 10px',
          }}>
            {t('app.adminPanel')}
          </a>
        )}
        <div style={{ flex: 1 }} />
        
        {/* Wallet (Ta Feito) — clique abre o extrato de movimentação */}
        {character && (
          <div
            className={styles.walletWrap}
            role="button"
            tabIndex={0}
            style={{ cursor: 'pointer' }}
            onClick={() => setShowWalletHistory(true)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setShowWalletHistory(true); }}
            title={t('wallet.history.title' as any)}
          >
            <span>💰</span>
            <span style={{ fontWeight: 800 }}>{walletBalance.toLocaleString()}</span>
            <span style={{ fontSize: 10, fontWeight: 700, color: '#7aaa7a' }}>TF</span>
            <div className={styles.walletTooltip}>
              <span className={styles.walletName}>{t('economy.currency' as any)}</span>
              <span className={styles.walletValue}>{walletBalance.toLocaleString()} TF</span>
            </div>
          </div>
        )}

        {/* Energia — barra curta ao lado da moeda. Cor de energia (ciano/azul),
            distinta da vida. Hover mostra o tempo que falta para 100%. */}
        {character && gameConfig && (
          <div
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}
            title={
              `${t('energy.label' as any) || 'Energia'}: ${Math.floor(visualEnergy)}/${gameConfig.maxEnergy} — ` +
              (energyPct >= 100
                ? (t('energy.full' as any) || 'cheia')
                : `${fmtDur(secsToFull)} ${t('energy.toFull' as any) || 'para 100%'}`)
            }
          >
            <span style={{ fontSize: 13 }}>⚡</span>
            <div style={{
              width: 66, height: 8, borderRadius: 5, overflow: 'hidden',
              background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(120,200,255,0.3)',
            }}>
              <div style={{
                width: `${energyPct}%`, height: '100%',
                background: 'linear-gradient(90deg, #38d6e6, #3f92ff)',
                transition: 'width 0.3s ease',
              }} />
            </div>
            <span style={{ fontSize: 11, fontWeight: 800, color: '#8fd6ec', minWidth: 30 }}>
              {Math.round(energyPct)}%
            </span>
          </div>
        )}

        {/* Language toggle */}
        <div style={{ display: 'flex', gap: 4 }}>
          {(['en', 'pt'] as const).map((lang) => (
            <button
              key={lang}
              onClick={() => setLanguage(lang)}
              style={{
                background: language === lang ? 'rgba(200,160,60,0.3)' : 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.2)',
                color: language === lang ? '#fff' : '#ccc',
                padding: '2px 6px',
                borderRadius: '4px',
                fontSize: '11px',
                cursor: 'pointer'
              }}
            >
              {lang.toUpperCase()}
            </button>
          ))}
        </div>
        <button 
          className={styles.btnNavRanking}
          onClick={() => navigate('/ranking')}
        >
          🏆 {t('user.ranking')}
        </button>
        <span className={styles.navEmail}>{user?.email}</span>
        <button className={styles.btnLogout} onClick={handleLogout}>{t('user.logout')}</button>
      </nav>

      {battleErr && (
        <div
          role="alert"
          onClick={() => setBattleErr(null)}
          style={{
            position: 'fixed', top: 64, left: '50%', transform: 'translateX(-50%)',
            zIndex: 4000, maxWidth: 'min(520px, calc(100vw - 24px))',
            background: 'rgba(120, 30, 30, 0.96)', border: '1px solid rgba(255,120,120,0.5)',
            color: '#ffdede', padding: '10px 16px', borderRadius: 10, fontSize: 13,
            boxShadow: '0 10px 30px rgba(0,0,0,0.5)', cursor: 'pointer', textAlign: 'center',
          }}
        >
          ⚡ {battleErr} <span style={{ opacity: 0.6, marginLeft: 6 }}>(fechar)</span>
        </div>
      )}

      <main className={`${styles.main} ${isMapView ? styles.mainMap : ''}`}>

        {charLoading ? (
          /* ── Skeleton ── */
          <div className={styles.skeleton} />

        ) : character ? (
          /* ── Has character — dashboard ── */
          isMapView ? (
             <HubMap 
               token={token!} 
               hubState={hubState} 
               character={character}
               battling={battling}
               onStartBattle={handleHubBattle}
             />
          ) : (
          <>
            <div className={styles.dashboardGrid}>
            
            <div className={styles.leftColumn}>
              {/* Character card */}
              <div
                className={styles.characterCard}
                style={{ '--class-color': classMeta?.color ?? '#c8a040', cursor: 'pointer' } as React.CSSProperties}
                onClick={() => setShowStatsModal(true)}
              >
                <span className={styles.charIcon}>
                  {classMeta?.icon ?? '⚔'}
                  {!!character.unspentAttributePoints && character.unspentAttributePoints > 0 && (
                    <div className={styles.levelUpAlert} title="Pontos de evolução disponíveis!">!</div>
                  )}
                </span>
                <div className={styles.charInfo}>
                  <h1 className={styles.charName}>{character.name}</h1>
                  <span className={styles.charClass} style={{ color: classMeta?.color }}>
                    {t(`class.${character.class}` as any) || character.class}
                  </span>
                  <span className={styles.charMeta}>
                    {t('user.charSince')} {new Date(character.createdAt).toLocaleDateString('pt-BR')}
                  </span>
                  <span style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 4, display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <img src={profileIcon} alt="" style={{ width: '14px', height: '14px' }} />
                    {t('profile.view') || 'Clique para ver atributos'}
                  </span>
                </div>

                {/* Controles do jogador — iguais aos do Mapa Hub */}
                {token && character && (
                  <div className={styles.cardBackpack} onClick={(e) => e.stopPropagation()}>
                    <PlayerToolbar
                      token={token}
                      character={character}
                      classColor={classMeta?.color ?? '#c8a040'}
                      onCharacterUpdate={setCharacter}
                      variant="inline"
                      showFicha={false}
                    />
                  </div>
                )}
              </div>

              {/* ── Linha de ação: apenas Batalha (mochila está no card acima) ── */}
              <div className={styles.actionRow}>
                  <div className={styles.battleSection}>
                    <p className={styles.battleHint} dangerouslySetInnerHTML={{ __html: t('user.battleHint') }} />

                    {gameConfig && (
                      <div className={styles.energyBarContainer}>
                        <div className={styles.energyBarLabel}>
                          <span>⚡ {t('energy.label' as any)}</span>
                          <span>{Math.floor(visualEnergy)} / {gameConfig.maxEnergy}</span>
                        </div>
                        <div className={styles.energyBarBg}>
                          <div 
                            className={styles.energyBarFill} 
                            style={{ width: `${Math.min(100, Math.max(0, (visualEnergy / gameConfig.maxEnergy) * 100))}%` }} 
                          />
                        </div>
                        {!hasEnoughEnergy && (
                          <span className={styles.battleCostWarning}>
                            {t('energy.insufficient' as any)} ({t('energy.cost' as any)}: {gameConfig.battleEnergyCost})
                          </span>
                        )}
                      </div>
                    )}

                    {battleInfo && (
                      <div className={styles.battleFound}>
                        <span className={styles.battleFoundDot} />
                        {battleInfo} — a entrar no combate…
                      </div>
                    )}

                    {battleErr && <p className={styles.error}>{battleErr}</p>}

                    {myParty && (
                      <p className={styles.battlePartyNote}>
                        👥 {t('party.soloWhileInTeam')}
                      </p>
                    )}

                    <button
                      className={`${styles.battleBtn} ${!hasEnoughEnergy ? styles.disabledBtn : ''}`}
                      onClick={() => handleBattle(false)}
                      disabled={battling || !hasEnoughEnergy}
                    >
                      {battling ? (
                        <>
                          <span className={styles.battleSpinner} />
                          {t('user.preparing')}
                        </>
                      ) : (
                        `⚔  ${t('user.battleBtn')}`
                      )}
                    </button>

                    <p className={styles.battleFootnote}>
                      {t('user.battleFootnote')}
                    </p>
                  </div>
              </div>
            </div>

            <div className={styles.rightColumn}>
              {/* Party section */}
              {token && <PartySection token={token} partyState={partyState} />}

              {/* Friends section */}
              {token && <FriendsSection token={token} />}
            </div>

          </div>

            {/* Character Stats Modal */}
            {showStatsModal && character && (
              <CharacterStatsModal
                character={character}
                classColor={classMeta?.color ?? '#c8a040'}
                token={token ?? undefined}
                onClose={() => setShowStatsModal(false)}
                onUpdate={setCharacter}
              />
            )}

            {/* Extrato da carteira "Ta Feito" */}
            {showWalletHistory && token && (
              <WalletHistoryModal token={token} onClose={() => setShowWalletHistory(false)} />
            )}
          </>
          )

        ) : (
          /* ── No character — creation form ── */
          <div className={styles.createSection}>
            <div className={styles.createHeader}>
              <span className={styles.createEmblem}>🗡</span>
              <h1 className={styles.createTitle}>{t('user.createChar')}</h1>
              <p className={styles.createHint} dangerouslySetInnerHTML={{ __html: t('user.createHint') }} />
            </div>

            <form className={styles.createForm} onSubmit={handleCreate}>
              {/* Name */}
              <div className={styles.field}>
                <label className={styles.fieldLabel}>{t('user.charName')}</label>
                <input
                  type="text"
                  className={styles.input}
                  placeholder="Ex: Artorias"
                  value={charName}
                  onChange={e => setCharName(e.target.value)}
                  maxLength={30}
                  required
                />
              </div>

              {/* Class picker */}
              <div className={styles.field}>
                <label className={styles.fieldLabel}>{t('user.class')}</label>
                <div className={styles.classGrid}>
                  {CLASSES.map(cls => (
                    <button
                      key={cls.id}
                      type="button"
                      className={styles.classCard}
                      data-selected={charClass === cls.id ? 'true' : 'false'}
                      onClick={() => setCharClass(cls.id)}
                    >
                      <span className={styles.classIcon}>{cls.icon}</span>
                      <span className={styles.className}>{t(`class.${cls.id}` as any) || cls.label}</span>
                      <span className={styles.classDesc}>{t(`class.${cls.id}.desc` as any)}</span>
                    </button>
                  ))}
                </div>
              </div>

              {createErr && <p className={styles.error}>{createErr}</p>}

              <button
                type="submit"
                className={styles.btnPrimary}
                disabled={creating || !charName.trim()}
              >
                {creating ? t('user.creating') : t('user.createChar')}
              </button>
            </form>
          </div>
        )}

      </main>
    </div>
  );
}
